import { driver } from 'driver.js';
import { useCallback, useEffect, useRef } from 'react';
import i18n from '../../../../i18n';
import { TourKey } from '../../../common/v2/tours/tours';
import { useSeenTour } from '../../../common/v2/tours/useSeenTour';

const SEL = {
  intro: '[data-pbs-drilldown-tour="modal-intro"]',
  columns: '[data-pbs-drilldown-tour="modal-columns"]',
  table: '[data-pbs-drilldown-tour="modal-table"]',
  value: '[data-pbs-drilldown-tour="modal-value"]',
  valueFilter: '[data-pbs-drilldown-tour="modal-value-filter"]',
  search: '[data-pbs-drilldown-tour="modal-search"]',
} as const;

/** Reset modal scroll so the Employee ID header (sticky table header) is not off-screen after table steps. */
function resetModalScrollForTourStep(anchorEl: Element) {
  for (
    let el: Element | null = anchorEl.parentElement;
    el;
    el = el.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(el);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      el.scrollTop = 0;
    }
  }
  anchorEl.scrollIntoView({ block: 'start', inline: 'nearest' });
}

type TourInput = {
  open: boolean;
  loading: boolean;
  error: string | null;
  /** Underlying list from the server (modal content is meaningful). */
  employeeCount: number;
  /** Table grid is mounted (has at least one row to show). */
  tableInDom: boolean;
};

/**
 * driver.js tour for the PBS cell drill-down modal.
 *
 * Auto-runs once ever per user (BE-backed "seen" flag), and exposes `startTour`
 * so the top-bar replay button can run it on demand regardless of that flag.
 */
export function usePbsCellDrillDownModalTour(input: TourInput) {
  const destroyRef = useRef<(() => void) | null>(null);
  const { seen, markSeen } = useSeenTour(TourKey.PbsCellDrillDownModal);

  const runTour = useCallback(() => {
    const introEl = document.querySelector(SEL.intro);
    const searchEl = document.querySelector(SEL.search);
    if (!introEl || !searchEl) {
      return;
    }

    // Tear down any in-flight tour before starting a fresh one (e.g. replay).
    destroyRef.current?.();
    destroyRef.current = null;

    type DriverStep = {
      element: string;
      onHighlightStarted?: (element: Element | undefined) => void;
      popover: {
        title: string;
        description: string;
        side: 'bottom' | 'top' | 'left' | 'right';
        align: 'start' | 'center' | 'end';
      };
    };

    const steps: DriverStep[] = [];

    if (input.tableInDom && document.querySelector(SEL.table)) {
      steps.push({
        element: SEL.table,
        popover: {
          title: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.tableTitle',
            { defaultValue: 'Employee table' }
          ),
          description: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.tableDescription',
            {
              defaultValue:
                'Scroll to browse rows, or click Load more for the next chunk. Export downloads the current view.',
            }
          ),
          side: 'top',
          align: 'center',
        },
      });
    }

    if (document.querySelector(SEL.columns)) {
      steps.push({
        element: SEL.columns,
        popover: {
          title: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.columnsTitle',
            { defaultValue: 'Choose columns' }
          ),
          description: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.columnsDescription',
            { defaultValue: 'Column selection is saved automatically.' }
          ),
          side: 'bottom',
          align: 'end',
        },
      });
    }

    if (document.querySelector(SEL.valueFilter)) {
      steps.push({
        element: SEL.valueFilter,
        popover: {
          title: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.valueFilterStepTitle',
            { defaultValue: 'Filter the value' }
          ),
          description: i18n.t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.valueFilterStepDescription',
            {
              defaultValue:
                'Click this icon to only show employees whose value meets a comparison you set.',
            }
          ),
          side: 'bottom',
          align: 'end',
        },
      });
    }

    steps.push({
      element: SEL.search,
      onHighlightStarted: (element) => {
        if (element) resetModalScrollForTourStep(element);
      },
      popover: {
        title: i18n.t(
          'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.searchTitle',
          { defaultValue: 'Filter by Employee ID' }
        ),
        description: i18n.t(
          'pages:dashboards.peopleBalanceSheet.cellDrillDown.modalTour.searchDescription',
          {
            defaultValue:
              'Click the filter icon to narrow this list by employee ID (substring match).',
          }
        ),
        side: 'bottom',
        align: 'start',
      },
    });

    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.55)',
      stagePadding: 8,
      stageRadius: 4,
      popoverClass: 'pbs-tour-popover',
      steps,
    });

    destroyRef.current = () => {
      driverObj.destroy();
    };

    driverObj.drive();
  }, [input.tableInDom]);

  useEffect(() => {
    if (!input.open) {
      destroyRef.current?.();
      destroyRef.current = null;
      return;
    }

    if (input.loading || input.error != null || input.employeeCount < 1) {
      return;
    }

    if (seen) return;

    const timeout = window.setTimeout(() => {
      if (
        !document.querySelector(SEL.intro) ||
        !document.querySelector(SEL.search)
      ) {
        return;
      }
      markSeen();
      runTour();
    }, 700);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    input.open,
    input.loading,
    input.error,
    input.employeeCount,
    input.tableInDom,
    seen,
    markSeen,
    runTour,
  ]);

  useEffect(() => {
    return () => {
      destroyRef.current?.();
      destroyRef.current = null;
    };
  }, []);

  return { startTour: runTour };
}
